/**
 * Simulator panel (P-1.8) — path + per-node inspector + fixtures.
 *
 * Invoked from the toolbar's "Simulate" button. Renders as a bottom drawer
 * over the canvas. Running fires `apiClient.simulator.run()` which resolves
 * client-side via the evaluator. The resulting path is lit on the canvas.
 */

import { useEffect, useMemo, useState } from "react";
import { Play, Save, X, ChevronDown, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import { getApiClient } from "../../lib/apiClient";
import { useEditorStore } from "../designer/editorStore";
import type { SimulatorResult } from "../../types/flow";
import type { FlowFixture } from "../../types/api";
import { useSimulatorStore } from "./simulatorStore";
import { useRunsStore } from "../runs/runsStore";

export function SimulatorPanel() {
  const open = useSimulatorStore((s) => s.open);
  const close = useSimulatorStore((s) => s.close);
  const flowId = useEditorStore((s) => s.flowId);
  const [fixtures, setFixtures] = useState<FlowFixture[]>([]);
  const [contextText, setContextText] = useState(`{
  "buyer_country": "FR",
  "cart_total_raw": 15000,
  "currency": "EUR",
  "returning_customer": false
}
`);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SimulatorResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const [saveName, setSaveName] = useState("");

  const parsedContext = useMemo(() => {
    try {
      const p = JSON.parse(contextText);
      setParseError(null);
      return p as Record<string, unknown>;
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "invalid JSON");
      return null;
    }
  }, [contextText]);

  useEffect(() => {
    if (!open || !flowId) return;
    getApiClient()
      .fixtures.list(flowId)
      .then(setFixtures);
  }, [open, flowId]);

  // Surface the path to the canvas by setting a CSS class on nodes/edges
  useEffect(() => {
    if (!result) {
      useEditorStore.getState().setNodes((ns) =>
        ns.map((n) => ({ ...n, className: undefined })),
      );
      useEditorStore.getState().setEdges((es) =>
        es.map((e) => ({ ...e, className: e.className?.replace(" edge-traced", "") })),
      );
      return;
    }
    const traced = new Set(result.path);
    useEditorStore.getState().setNodes((ns) =>
      ns.map((n) => ({
        ...n,
        className: traced.has(n.id) ? "traced" : "untraced",
      })),
    );
    useEditorStore.getState().setEdges((es) =>
      es.map((e) => {
        const isTraced = traced.has(e.source) && traced.has(e.target);
        const errBase = e.className?.includes("edge-error") ? "edge-error" : "";
        return {
          ...e,
          className: [errBase, isTraced ? "edge-traced" : ""].filter(Boolean).join(" "),
        };
      }),
    );
  }, [result]);

  if (!open || !flowId) return null;

  async function run(trace: boolean) {
    if (!flowId || !parsedContext) return;
    setRunning(true);
    try {
      const res = await getApiClient().simulator.run(flowId, {
        context: parsedContext,
        trace_mode: trace,
      });
      setResult(res);
      setExpandedNode(res.path[res.path.length - 1] ?? null);
      useRunsStore.getState().recordRun({
        flowId,
        timestamp: new Date().toISOString(),
        status: res.error ? "error" : "success",
        endNodeId: res.final_node_id ?? res.error?.node_id,
        pathLength: res.path.length,
        errorCode: res.error?.code,
        errorMessage: res.error?.message,
      });
    } catch (err) {
      useRunsStore.getState().recordRun({
        flowId,
        timestamp: new Date().toISOString(),
        status: "error",
        errorCode: "RUN_FAILED",
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      throw err;
    } finally {
      setRunning(false);
    }
  }

  async function loadFixture(fx: FlowFixture) {
    setContextText(JSON.stringify(fx.context, null, 2));
  }

  async function saveAsFixture() {
    if (!flowId || !parsedContext || !saveName.trim()) return;
    await getApiClient().fixtures.create(flowId, {
      name: saveName.trim(),
      context: parsedContext,
    });
    setSaveName("");
    const list = await getApiClient().fixtures.list(flowId);
    setFixtures(list);
  }

  return (
    <div className="absolute inset-x-0 bottom-0 h-[45vh] bg-white dark:bg-neutral-950 border-t border-neutral-200 dark:border-neutral-800 shadow-xl flex flex-col z-20">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 dark:border-neutral-800">
        <span className="text-sm font-semibold">Simulator</span>
        <span className="text-xs text-neutral-500">
          Client-side JSONLogic evaluator
        </span>
        <div className="flex-1" />
        <button
          onClick={() => run(true)}
          disabled={running || !parsedContext}
          className="flex items-center gap-1 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-2.5 py-1 text-xs font-medium hover:bg-category-trigger/20 disabled:opacity-50"
        >
          <Play className="w-3.5 h-3.5" />
          Run with trace
        </button>
        <button
          onClick={close}
          className="ml-2 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          aria-label="Close simulator"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-3 gap-3 p-3 overflow-hidden">
        {/* Context editor + fixtures */}
        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase text-neutral-500 mb-1">
            Context
          </p>
          <textarea
            value={contextText}
            onChange={(e) => setContextText(e.target.value)}
            spellCheck={false}
            className="flex-1 min-h-0 w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1.5 text-xs font-mono"
          />
          {parseError ? (
            <p className="mt-1 text-[11px] text-category-error">{parseError}</p>
          ) : null}
          <div className="mt-2 flex items-center gap-1">
            <input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="fixture name"
              className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1 text-xs"
            />
            <button
              onClick={saveAsFixture}
              disabled={!saveName.trim() || !parsedContext}
              className="flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-xs disabled:opacity-50"
            >
              <Save className="w-3 h-3" />
              Save
            </button>
          </div>
          <div className="mt-2">
            <p className="text-[11px] font-semibold uppercase text-neutral-500 mb-1">Fixtures</p>
            {fixtures.length === 0 ? (
              <p className="text-xs text-neutral-500">No saved fixtures.</p>
            ) : (
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {fixtures.map((fx) => (
                  <li key={fx.id}>
                    <button
                      onClick={() => loadFixture(fx)}
                      className="w-full text-left text-xs rounded px-1.5 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                    >
                      {fx.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Path */}
        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase text-neutral-500 mb-1">Path</p>
          {!result ? (
            <p className="text-xs text-neutral-500">Run to see the path.</p>
          ) : result.error ? (
            <div className="rounded-md border border-category-error/40 bg-category-error/5 px-2 py-1.5 text-xs">
              <p className="font-medium text-category-error">{result.error.code}</p>
              <p className="mt-0.5">{result.error.message}</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-neutral-500 mb-1">
                Terminal: <span className="font-mono">{result.final_node_id ?? "—"}</span>
                {result.capture_mode ? ` · capture: ${result.capture_mode}` : ""}
              </p>
              <ul className="space-y-0.5 flex-1 min-h-0 overflow-y-auto">
                {result.path.map((nodeId, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => setExpandedNode(nodeId)}
                      className={clsx(
                        "w-full flex items-center gap-1 text-left text-xs rounded px-1.5 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-900",
                        expandedNode === nodeId && "bg-neutral-100 dark:bg-neutral-900",
                      )}
                    >
                      {expandedNode === nodeId ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                      <span className="font-mono">{nodeId}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Inspector */}
        <div className="flex flex-col min-h-0">
          <p className="text-xs font-semibold uppercase text-neutral-500 mb-1">Inspector</p>
          {!result || !expandedNode || !result.trace ? (
            <p className="text-xs text-neutral-500">Select a node in the path.</p>
          ) : (
            <NodeInspector trace={result.trace} nodeId={expandedNode} />
          )}
        </div>
      </div>
    </div>
  );
}

function NodeInspector({ trace, nodeId }: { trace: NonNullable<SimulatorResult["trace"]>; nodeId: string }) {
  const entries = trace.filter((e) => e.node_id === nodeId);
  if (entries.length === 0) {
    return <p className="text-xs text-neutral-500">No trace entry for this node.</p>;
  }
  // If a node was traversed multiple times, show the last entry.
  const entry = entries[entries.length - 1];
  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-2 text-xs">
      <div>
        <p className="font-semibold text-neutral-500">Entered at</p>
        <p className="font-mono">{entry.entered_at}</p>
      </div>
      <div>
        <p className="font-semibold text-neutral-500">Duration</p>
        <p className="font-mono">{entry.duration_us} µs</p>
      </div>
      {entry.rule_pretty ? (
        <div>
          <p className="font-semibold text-neutral-500">Rule</p>
          <pre className="rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-1.5 overflow-x-auto text-[11px]">
            {entry.rule_pretty}
          </pre>
        </div>
      ) : null}
      <div>
        <p className="font-semibold text-neutral-500">Output</p>
        <pre className="rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-1.5 overflow-x-auto text-[11px]">
          {JSON.stringify(entry.output_value)}
        </pre>
      </div>
      {entry.error ? (
        <div>
          <p className="font-semibold text-category-error">Error</p>
          <p className="text-category-error">{entry.error}</p>
        </div>
      ) : null}
      <details>
        <summary className="cursor-pointer text-neutral-500 font-semibold">
          Input context snapshot
        </summary>
        <pre className="mt-1 rounded border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-1.5 overflow-x-auto text-[11px]">
          {JSON.stringify(entry.input_context_snapshot, null, 2)}
        </pre>
      </details>
    </div>
  );
}
